import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Divider, Skeleton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useFormatter } from '../../context/FormatterContext';
import { useDashboardData } from '../../hooks/useDashboardData';

interface StatItemProps {
    label: string;
    value: string | React.ReactNode;
    subValue?: string;
    hasInfo?: boolean;
    tooltipText?: string;
    isColored?: boolean;
    isPositiveValue?: boolean;
    isLoading?: boolean;
}

const StatItem: React.FC<StatItemProps> = ({
    label,
    value,
    subValue,
    hasInfo,
    tooltipText,
    isColored,
    isPositiveValue,
    isLoading = false,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isPositive = subValue?.startsWith('+');
    const isNegative = subValue?.startsWith('-');

    // Determine style based on isColored and value sign
    const getValueStyle = () => {
        if (!isColored) {
            return {
                fontWeight: 'bold',
                textAlign: 'right' as const,
                fontSize: '1rem',
                color: theme.palette.text.primary,
            };
        }

        return isPositiveValue 
            ? {
                fontWeight: 'bold',
                textAlign: 'right' as const,
                fontSize: '1rem',
                color: isDarkMode ? '#23ef37' : '#2e7d32',
              }
            : {
                fontWeight: 'bold',
                textAlign: 'right' as const,
                fontSize: '1rem',
                color: isDarkMode ? '#ef2323' : '#d32f2f',
              };
    };

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 1.5,
            width: '100%',
            alignItems: 'center',
        }}>
            {/* Left side - Label with info icon */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: '60%',
                overflow: 'hidden',
            }}>
                {label.length > 18 ? (
                    <Tooltip title={label}>
                        <Typography 
                            noWrap
                            variant="body2" 
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                mr: 0.5,
                            }}
                        >
                            {label}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography 
                        noWrap
                        variant="body2" 
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            mr: 0.5,
                        }}
                    >
                        {label}
                    </Typography>
                )}
                {hasInfo && tooltipText && (
                    <Tooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {tooltipText}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    maxWidth: 400,
                                    width: 'auto',
                                    p: 0,
                                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                                    backdropFilter: 'blur(5px)',
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => alpha(theme.palette.background.paper, 0.9),
                                    }
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    }
                                }
                            }
                        }}
                    >
                        <InfoOutlinedIcon sx={{
                            flexShrink: 0,
                            fontSize: '1.1rem',
                            color: theme.palette.text.secondary,
                            mb: 0.4,
                            ml: 0.2,
                            cursor: 'help',
                        }} />
                    </Tooltip>
                )}
            </Box>
            
            {/* Right side - Value and subvalue */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                flexShrink: 0, // Prevent shrinking
            }}>
                {isLoading ? (
                    <Skeleton width="120px" height={24} />
                ) : (
                    <>
                        <Typography variant="body2" sx={getValueStyle()}>
                            {value}
                        </Typography>
                        {subValue && (
                            <Typography
                                variant="body2"
                                sx={
                                    isPositive
                                        ? {
                                            textAlign: 'right',
                                            fontSize: '0.875rem',
                                            fontWeight: 'bold',
                                            color: isDarkMode ? '#23ef37' : '#2e7d32',
                                          }
                                        : isNegative
                                          ? {
                                              textAlign: 'right',
                                              fontSize: '0.875rem',
                                              fontWeight: 'bold',
                                              color: isDarkMode ? '#ef2323' : '#d32f2f',
                                            }
                                          : {
                                              textAlign: 'right',
                                              fontSize: '0.875rem',
                                              fontWeight: 'bold',
                                              color: theme.palette.text.secondary,
                                            }
                                }
                            >
                                {subValue}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

const DashboardOverview: React.FC<{ balance: number }> = ({ balance }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { formatCurrency, formatPercentage } = useFormatter();
    const {
        totalProceeds,
        isTotalProceedsLoading,
        fiatInvested,
        isFiatInvestedLoading,
        overviewData,
        isOverviewDataLoading,
        unrealizedGains,
        isUnrealizedGainsLoading,
    } = useDashboardData();

    return (
        <Box
            sx={{
                pt: 2.25,
                pb: 2.25,
                px: 4,
                height: 'auto',
                minHeight: '0',
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
            }}
        >
            <StatItem
                label={t('dashboard.overview.balance')}
                value={balance ? formatCurrency(balance) : '-'}
                hasInfo
                tooltipText={t('dashboard.overview.tooltips.balance')}
                isLoading={!balance}
            />
            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
            
            <StatItem
                label={t('dashboard.overview.fiatProceeds')}
                value={formatCurrency(totalProceeds?.totalProceeds)}
                hasInfo
                tooltipText={t('dashboard.overview.tooltips.fiatProceeds')}
                isLoading={isTotalProceedsLoading}
            />
            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
            
            <StatItem
                label={t('dashboard.overview.fiatInvested')}
                value={formatCurrency(fiatInvested?.fiatInvested)}
                hasInfo
                tooltipText={t('dashboard.overview.tooltips.fiatInvested')}
                isLoading={isFiatInvestedLoading}
            />
            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />

            <StatItem
                label={t('dashboard.overview.totalReturn')}
                value={formatCurrency(overviewData?.totalReturn || 0)}
                hasInfo
                tooltipText={t('dashboard.overview.tooltips.totalReturn')}
                isColored
                isPositiveValue={(overviewData?.totalReturn || 0) >= 0}
                isLoading={isOverviewDataLoading}
            />
            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
            
            <StatItem
                label={t('dashboard.overview.roi')}
                value={formatPercentage(overviewData?.roi || 0)}
                hasInfo
                tooltipText={t('dashboard.overview.tooltips.roi')}
                isColored
                isPositiveValue={(overviewData?.roi || 0) >= 0}
                isLoading={isOverviewDataLoading}
            />
            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
            <StatItem
                label={t('dashboard.overview.unrealizedGains')}
                value={formatCurrency(unrealizedGains?.unrealizedGains || 0)}
                hasInfo
                tooltipText={t('dashboard.overview.tooltips.unrealizedGains')}
                isColored
                isPositiveValue={(unrealizedGains?.unrealizedGains || 0) >= 0}
                isLoading={isUnrealizedGainsLoading}
            />
        </Box>
    );
};

export default DashboardOverview;
